package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.MenuContentAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst

/**
 * 组件展开期的「元素树 / 表达式 / 语句」深度改写器。
 *
 * 同一套遍历骨架被两类改写复用（[Substitution] 策略只替换叶子语义，避免两份近似遍历的复制粘贴）：
 * - [PropsSubstitution]：把组件 root 内 `props.xxx` 引用替换为调用方提供的实际表达式（无状态）。
 * - [StateNameSubstitution]：把组件 root 内 `state.field` 读写改名为 `instanceId/field`，
 *   配合 [ComponentExpanderPass] 的 state 提升，让每个组件实例拥有独立 state 作用域。
 *
 * 无状态、纯函数实现。
 */
internal object ExpressionSubstitutor {

    // ===== 改写策略：仅决定叶子语义，结构递归由本对象统一负责 =====

    /**
     * 叶子改写策略。结构递归（children / tooltip / menu / lambda / when / stmt 等）由
     * [ExpressionSubstitutor] 统一负责；策略只回答两个问题：
     * - 当前表达式叶子是否需要替换（[onExprLeaf]）；
     * - 赋值左值是否需要改名（[rewriteLValue]）。
     */
    private interface Substitution {
        /** 命中需替换的叶子时返回替换后表达式；否则返回 null 交回结构递归。 */
        fun onExprLeaf(expr: ExprAst): ExprAst?

        /**
         * 改写标量赋值左值（StateLValue/SessionLValue/... 等不含子表达式的左值）；
         * 默认恒等（props 替换不动左值），state 改名覆写之。
         * 索引左值 IndexLValue 的子表达式递归由 [ExpressionSubstitutor.substituteLValue] 统一负责，不经此方法。
         */
        fun rewriteLValue(lv: LValueAst): LValueAst = lv

        /**
         * 是否启用「props.onClick() 展开后的叶子可调用折叠」。
         *
         * 仅 props 替换需要：组件 root 内写 `onClick = props.onClick()`、调用方传入
         * `onClicked()` 时，展开后应折叠掉外层空 CallExpr，避免双层包裹。state 改名无此需求。
         */
        val collapsesLeafCallable: Boolean
    }

    /** PropsRef → 实参表达式替换策略，保持组件展开既有语义。 */
    private class PropsSubstitution(private val propMap: Map<String, ExprAst>) : Substitution {
        override val collapsesLeafCallable: Boolean = true
        override fun onExprLeaf(expr: ExprAst): ExprAst? =
            if (expr is ExprAst.PropsRef) propMap[expr.name] ?: expr else null
    }

    /**
     * StateRef / StateLValue 改名策略：把组件 [声明的] state 字段名改写为 `instanceId/field`。
     *
     * 只改写出现在组件 state 声明里的字段名（[fieldNames]）。这样组件 root 内引用 screen 级或
     * 未声明 state 时不会被误改名（理论上组件内只应引用自身 state，此约束作为额外保险）。
     */
    private class StateNameSubstitution(
        private val instanceId: String,
        private val fieldNames: Set<String>,
    ) : Substitution {
        override val collapsesLeafCallable: Boolean = false

        private fun mangle(field: String): String = "$instanceId/$field"

        override fun onExprLeaf(expr: ExprAst): ExprAst? =
            if (expr is ExprAst.StateRef && expr.name in fieldNames) {
                expr.copy(name = mangle(expr.name))
            } else {
                null
            }

        override fun rewriteLValue(lv: LValueAst): LValueAst =
            if (lv is LValueAst.StateLValue && lv.name in fieldNames) {
                lv.copy(name = mangle(lv.name))
            } else {
                lv
            }
    }

    // ===== 对外入口 =====

    /** 递归替换元素树中所有 PropsRef 为实际表达式。 */
    fun substituteElement(element: ElementAst, propMap: Map<String, ExprAst>): ElementAst =
        substituteElement(element, PropsSubstitution(propMap))

    /**
     * 把组件 root 内对 [fieldNames] 中字段的 `state.field` 读写改名为 `instanceId/field`。
     *
     * 与 [substituteElement] 复用同一遍历骨架，覆盖与 PropsRef 完全相同的全部位置
     * （props / children / namedSlotContent / canvas / tooltip / toast / menu /
     *  conditional / for / lambda / if / when / stmt 子树）。[fieldNames] 为空时直接原样返回。
     */
    fun substituteStateNames(
        element: ElementAst,
        instanceId: String,
        fieldNames: Set<String>,
    ): ElementAst {
        if (fieldNames.isEmpty() || instanceId.isEmpty()) return element
        return substituteElement(element, StateNameSubstitution(instanceId, fieldNames))
    }

    // ===== 元素树结构递归 =====

    private fun substituteElement(element: ElementAst, sub: Substitution): ElementAst {
        val newProps = element.props.map { pa ->
            pa.copy(value = substituteExpr(pa.value, sub))
        }
        val newModifiers = element.modifiers.map { modifier ->
            modifier.copy(fields = modifier.fields.map { it.copy(value = substituteExpr(it.value, sub)) })
        }
        val newChildren = element.children.map { substituteElement(it, sub) }
        // 风险 4 遍历审计：组件 root 内嵌套组件调用的具名插槽填充内容（`Inner { slot "h" { ...props.x... } }`）
        // 同样可能引用外层组件的 props / state，必须在替换期一并下钻，否则残留到展开后致 E311/渲染异常。
        val newNamedSlotContent = if (element.namedSlotContent.isEmpty()) {
            element.namedSlotContent
        } else {
            element.namedSlotContent.mapValues { (_, content) -> content.map { substituteElement(it, sub) } }
        }
        // canvas 元素需要把 attrs / draw / once 内出现的引用一并替换，
        // 否则诸如 `arc(..., props.color)` / `state.x` 会带着引用残留到 codegen。
        val newCanvasData = element.canvasData?.let { canvas ->
            canvas.copy(
                width = substituteExpr(canvas.width, sub),
                height = substituteExpr(canvas.height, sub),
                durationMs = canvas.durationMs?.let { substituteExpr(it, sub) },
                attrs = canvas.attrs.map { it.copy(value = substituteExpr(it.value, sub)) },
                drawBlock = substituteDrawBlock(canvas.drawBlock, sub),
                onceBlock = canvas.onceBlock?.let { substituteDrawBlock(it, sub) },
            )
        }
        // 组件 root 内可能在以下位置直接引用 props.xxx / state.x：
        // 1) tooltip { text(props.title) } / tooltip { Component(arg = props.x) } / tooltip { box {...} }
        // 2) toast { ... } —— 同上三种形态
        // 3) rightClickMenu { Component(arg = props.x) } / contextMenu { ... }
        // 4) menu "id" { ... root 内引用 props.x ... }
        // 5) if (props.cond) { box {...} } else if (props.other) { ... } —— conditionalContent
        // 这些字段如不递归替换，组件实例化后会把引用残留到 IR/codegen。
        val newTooltipContent = element.tooltipContent?.let { substituteTooltipContent(it, sub) }
        val newToastContent = element.toastContent?.let { substituteTooltipContent(it, sub) }
        val newRightClickMenuContent = element.rightClickMenuContent?.let {
            substituteTooltipContent(it, sub) as TooltipContentAst.Component
        }
        val newContextMenuContent = element.contextMenuContent?.let {
            substituteTooltipContent(it, sub) as TooltipContentAst.Component
        }
        val newMenuBlocks = element.menuBlocks.map { substituteMenuContent(it, sub) }
        val newConditionalContent = element.conditionalContent?.let {
            substituteConditionalContent(it, sub)
        }
        val newForContent = element.forContent?.let {
            substituteForContent(it, sub)
        }
        // 数据驱动容器（`column "x" { entry -> source = props.list }`）的 source 表达式同样可能
        // 引用组件 props / state，必须一并替换。否则与 props["source"] 同步存在的 sourceExpr 字段
        // 残留 PropsRef，漏到 codegen 触发 IllegalStateException（与 PropValidator 下钻 sourceExpr 对称）。
        val newSourceExpr = element.sourceExpr?.let { substituteExpr(it, sub) }
        return element.copy(
            props = newProps,
            modifiers = newModifiers,
            children = newChildren,
            namedSlotContent = newNamedSlotContent,
            canvasData = newCanvasData,
            tooltipContent = newTooltipContent,
            toastContent = newToastContent,
            rightClickMenuContent = newRightClickMenuContent,
            contextMenuContent = newContextMenuContent,
            menuBlocks = newMenuBlocks,
            conditionalContent = newConditionalContent,
            forContent = newForContent,
            sourceExpr = newSourceExpr,
        )
    }

    /** 替换 [TooltipContentAst] 三种形态内的所有引用。 */
    private fun substituteTooltipContent(
        content: TooltipContentAst,
        sub: Substitution,
    ): TooltipContentAst = when (content) {
        is TooltipContentAst.Text -> content.copy(value = substituteExpr(content.value, sub))
        is TooltipContentAst.Inline -> content.copy(root = substituteElement(content.root, sub))
        is TooltipContentAst.Component -> content.copy(
            props = content.props.map { it.copy(value = substituteExpr(it.value, sub)) },
        )
    }

    /** 替换 [MenuContentAst] 块内的所有引用（块上 props 与 root 元素树）。 */
    private fun substituteMenuContent(
        menu: MenuContentAst,
        sub: Substitution,
    ): MenuContentAst = menu.copy(
        props = menu.props.map { it.copy(value = substituteExpr(it.value, sub)) },
        root = substituteElement(menu.root, sub),
    )

    /** 替换 [ConditionalContentAst] 内的所有引用（subject / 各 branch / else 分支体）。 */
    private fun substituteConditionalContent(
        content: ConditionalContentAst,
        sub: Substitution,
    ): ConditionalContentAst = content.copy(
        subject = content.subject?.let { substituteExpr(it, sub) },
        branches = content.branches.map { br ->
            br.copy(
                condition = br.condition?.let { substituteExpr(it, sub) },
                values = br.values.map { substituteExpr(it, sub) },
                body = substituteElement(br.body, sub),
            )
        },
        elseBranch = content.elseBranch?.let { substituteElement(it, sub) },
    )

    /** 替换 [ForContentAst] 内的所有引用（iterable 表达式 + 循环体）。 */
    private fun substituteForContent(
        content: ForContentAst,
        sub: Substitution,
    ): ForContentAst = content.copy(
        iterable = substituteExpr(content.iterable, sub),
        body = substituteElement(content.body, sub),
    )

    /** 递归替换 [DrawAst] 内所有指令上的引用。 */
    private fun substituteDrawBlock(drawAst: DrawAst, sub: Substitution): DrawAst {
        return drawAst.copy(instructions = drawAst.instructions.map { substituteDrawInstruction(it, sub) })
    }

    /** 替换单条 draw 指令携带的所有表达式（含嵌套 for/if 体）。 */
    private fun substituteDrawInstruction(
        ins: DrawInstructionAst,
        sub: Substitution,
    ): DrawInstructionAst = when (ins) {
        is DrawInstructionAst.Primitive -> ins.copy(
            args = ins.args.map { substituteExpr(it, sub) },
        )
        is DrawInstructionAst.ValAssign -> ins.copy(
            value = substituteExpr(ins.value, sub),
        )
        is DrawInstructionAst.For -> ins.copy(
            body = ins.body.map { substituteDrawInstruction(it, sub) },
        )
        is DrawInstructionAst.IfElse -> ins.copy(
            condition = substituteExpr(ins.condition, sub),
            thenBranch = ins.thenBranch.map { substituteDrawInstruction(it, sub) },
            elseBranch = ins.elseBranch.map { substituteDrawInstruction(it, sub) },
        )
    }

    // ===== 表达式结构递归 =====

    /** 深度替换表达式中所有命中策略的叶子节点。 */
    private fun substituteExpr(expr: ExprAst, sub: Substitution): ExprAst {
        // 叶子命中优先：策略命中即短路，不再结构递归（叶子无子表达式）。
        sub.onExprLeaf(expr)?.let { return it }
        return when (expr) {
            is ExprAst.BinaryOp -> expr.copy(
                left = substituteExpr(expr.left, sub),
                right = substituteExpr(expr.right, sub),
            )
            is ExprAst.UnaryOp -> expr.copy(operand = substituteExpr(expr.operand, sub))
            is ExprAst.MemberAccess -> expr.copy(target = substituteExpr(expr.target, sub))
            is ExprAst.SafeMemberAccess -> expr.copy(target = substituteExpr(expr.target, sub))
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> expr.copy(target = substituteExpr(expr.target, sub))
            is ExprAst.MemberCall -> expr.copy(
                target = substituteExpr(expr.target, sub),
                args = expr.args.map { substituteExpr(it, sub) },
                namedArgs = expr.namedArgs.map { it.copy(value = substituteExpr(it.value, sub)) },
            )
            is ExprAst.SafeMemberCall -> expr.copy(
                target = substituteExpr(expr.target, sub),
                args = expr.args.map { substituteExpr(it, sub) },
            )
            is ExprAst.Elvis -> expr.copy(
                left = substituteExpr(expr.left, sub),
                right = substituteExpr(expr.right, sub),
            )
            is ExprAst.Ternary -> expr.copy(
                cond = substituteExpr(expr.cond, sub),
                thenExpr = substituteExpr(expr.thenExpr, sub),
                elseExpr = substituteExpr(expr.elseExpr, sub),
            )
            is ExprAst.Interp -> expr.copy(parts = expr.parts.map { part ->
                when (part) {
                    is ExprAst.Interp.InterpPart.ExprPart ->
                        ExprAst.Interp.InterpPart.ExprPart(substituteExpr(part.expr, sub))
                    is ExprAst.Interp.InterpPart.TextPart -> part
                }
            })
            is ExprAst.ListLiteralExpr -> expr.copy(elements = expr.elements.map { substituteExpr(it, sub) })
            is ExprAst.MapLiteralExpr -> expr.copy(
                entries = expr.entries.map { it.copy(value = substituteExpr(it.value, sub)) },
            )
            is ExprAst.RangeExpr -> expr.copy(
                start = substituteExpr(expr.start, sub),
                endValue = substituteExpr(expr.endValue, sub),
            )
            is ExprAst.IndexExpr -> expr.copy(
                target = substituteExpr(expr.target, sub),
                index = substituteExpr(expr.index, sub),
            )
            is ExprAst.CallExpr -> {
                val newCallee = substituteExpr(expr.callee, sub)
                val newArgs = expr.args.map { substituteExpr(it, sub) }
                // props 专属折叠：展开后 callee 变成"已经是一个调用"或"裸 Call"，且组件 root 里
                // 写的是 props.x()（外层 args 为空），则直接返回 callee 本身，
                // 避免出现 CallExpr 嵌套 CallExpr 的双层包裹。典型场景：
                //   组件 root 内 `onClick = props.onClick()` 被调用方传入
                //   `onClicked()`（即 CallExpr(FunRefExpr,[])）时，展开后应等价于直接绑定该入口。
                val isLeafCallable =
                    newCallee is ExprAst.CallExpr && newCallee.args.isEmpty()
                when {
                    sub.collapsesLeafCallable && isLeafCallable && newArgs.isEmpty() -> newCallee
                    // props 带参折叠：组件 root 内 `onClaim = props.onClaim(entry)`（Function prop 带参调用，
                    // 典型为数据列表逐项回调），调用方传入 `::screenFn` 时，折叠成偏应用 `::screenFn(entry)`。
                    // 等价于外壳里直接写 `::screenFn(entry)`：满足事件绑定规则（E301 只收无参调用/::fun/::fun(args)/1参lambda），
                    // 并在运行期按循环变量 entry 逐项精确回调。否则会残留 CallExpr(FunRef,[entry]) 被 E301 拒。
                    sub.collapsesLeafCallable && newCallee is ExprAst.FunRefExpr && newArgs.isNotEmpty() ->
                        newCallee.copy(boundArgs = newCallee.boundArgs + newArgs)
                    else -> expr.copy(callee = newCallee, args = newArgs)
                }
            }
            is ExprAst.BuiltinCallExpr -> expr.copy(args = expr.args.map { substituteExpr(it, sub) })
            // IfExpr 分支体语法上只允许单 ExprStmt；深度替换其 cond / 两支表达式
            // 中可能出现的引用，避免组件 root 内 `if (props.cond) state.x = props.a else state.x = props.b`
            // 这类写法把引用残留到 codegen。
            is ExprAst.IfExpr -> expr.copy(
                cond = substituteExpr(expr.cond, sub),
                thenBody = substituteStmtList(expr.thenBody, sub),
                elseBody = substituteStmtList(expr.elseBody, sub),
            )
            // D1：lambda 体深度替换；body 内的引用（含嵌套语句）一并替换。
            is ExprAst.LambdaExpr -> expr.copy(body = substituteStmtList(expr.body, sub))
            // D1：when 主体 / values / 分支 body / elseBody 全量替换。
            is ExprAst.WhenExpr -> expr.copy(
                subject = substituteExpr(expr.subject, sub),
                branches = expr.branches.map { br ->
                    br.copy(
                        values = br.values.map { substituteExpr(it, sub) },
                        body = substituteStmtList(br.body, sub),
                    )
                },
                elseBody = expr.elseBody?.let { substituteStmtList(it, sub) },
            )
            // D1：net.request 内 payload / callback / timeoutMs 全量替换。
            is ExprAst.NetRequestCall -> expr.copy(
                payload = expr.payload.map { it.copy(value = substituteExpr(it.value, sub)) },
                callback = expr.callback?.let { substituteExpr(it, sub) },
                timeoutMs = expr.timeoutMs?.let { substituteExpr(it, sub) },
            )
            // Stage B：net.subscribe handler 替换（namespace / topic 是字面量已固化，无需替换）
            is ExprAst.NetSubscribeCall -> expr.copy(
                handler = substituteExpr(expr.handler, sub),
            )
            // D1：await 内层 NetRequestCall + 可选 tailExpr 一起替换。
            is ExprAst.AwaitExpr -> expr.copy(
                netRequestCall = expr.netRequestCall?.let {
                    substituteExpr(it, sub)
                },
                suspendCall = expr.suspendCall?.let {
                    substituteExpr(it, sub) as ExprAst.CallExpr
                },
                tailExpr = expr.tailExpr?.let { substituteExpr(it, sub) },
            )
            // M6-e：FunRefExpr 的 boundArgs / boundNamedArgs（`::name(props.x)` 部分应用）
            // 在组件 root 内可能直接引用 props.xxx / state.x，需深度替换；纯引用形态（两者都为空）
            // 没有子表达式，原样返回。
            is ExprAst.FunRefExpr -> {
                if (expr.boundArgs.isEmpty() && expr.boundNamedArgs.isEmpty()) {
                    expr
                } else {
                    expr.copy(
                        boundArgs = expr.boundArgs.map { substituteExpr(it, sub) },
                        boundNamedArgs = expr.boundNamedArgs.map {
                            it.copy(value = substituteExpr(it.value, sub))
                        },
                    )
                }
            }
            // M5-c：IsExpr 仅替换 target 子表达式
            is ExprAst.IsExpr -> expr.copy(target = substituteExpr(expr.target, sub))
            // 叶子节点或不含子表达式的节点（StateRef/PropsRef 等若需替换已在上方 onExprLeaf 命中）
            is ExprAst.Literal, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef,
            is ExprAst.DerivedRef, is ExprAst.IdentRef, is ExprAst.PropsRef,
            is ExprAst.LocalRef, is ExprAst.ThemeRef,
            is ExprAst.NullLit -> expr
        }
    }

    /**
     * D1：覆盖全部 StmtAst 子类型的语句列表替换 helper。
     *
     * 与 walkStmtList 对应：每条语句的所有子表达式递归调用 substituteExpr，
     * 控制流块、try、when 分支体、局部 fun 体、await 降级 suffix 全部进入。
     */
    private fun substituteStmtList(stmts: List<StmtAst>, sub: Substitution): List<StmtAst> =
        stmts.map { substituteStmt(it, sub) }

    private fun substituteStmt(s: StmtAst, sub: Substitution): StmtAst = when (s) {
        is StmtAst.ExprStmt -> s.copy(call = substituteExpr(s.call, sub))
        // 赋值语句：rhs 永远替换；lhs 由 substituteLValue 处理（标量左值由策略决定是否改名，
        // 索引左值 `target[index] = rhs` 的 target/index 子表达式在此结构递归，
        // 使其内部 StateRef 与读路径一致改名，否则 state.field[i]=x 的写路径漏改名触发 DSL_E201）。
        is StmtAst.AssignStmt -> s.copy(lhs = substituteLValue(s.lhs, sub), rhs = substituteExpr(s.rhs, sub))
        is StmtAst.VarDeclStmt -> s.copy(initializer = substituteExpr(s.initializer, sub))
        is StmtAst.IfStmt -> substituteIfStmt(s, sub)
        is StmtAst.WhileStmt -> s.copy(cond = substituteExpr(s.cond, sub), body = substituteStmtList(s.body, sub))
        is StmtAst.ForInStmt -> s.copy(iterable = substituteExpr(s.iterable, sub), body = substituteStmtList(s.body, sub))
        is StmtAst.WhenStmt -> s.copy(whenExpr = substituteExpr(s.whenExpr, sub) as ExprAst.WhenExpr)
        is StmtAst.ReturnStmt -> s.copy(value = s.value?.let { substituteExpr(it, sub) })
        is StmtAst.TryStmt -> substituteTryStmt(s, sub)
        // batch 语句：对 body 递归替换并返回 copy（与 TryStmt 的 tryBody 处理一致）
        is StmtAst.BatchStmt -> s.copy(body = substituteStmtList(s.body, sub))
        is StmtAst.FunDeclStmt -> s.copy(decl = s.decl.copy(body = substituteStmtList(s.decl.body, sub)))
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> s
    }

    /**
     * 改写赋值左值。
     *
     * 职责切分与表达式一致：标量左值（StateLValue/SessionLValue/...）的「叶子语义」由策略
     * [Substitution.rewriteLValue] 决定（props 恒等不动、state 改名 StateLValue）；
     * 索引左值 `target[index] = rhs` 的「结构递归」由本对象统一负责——对 target/index 子表达式
     * 递归 substituteExpr，使其内部 StateRef 与读路径同样改名（修复 state.field[i]=x 写路径漏改名）。
     */
    private fun substituteLValue(lv: LValueAst, sub: Substitution): LValueAst = when (lv) {
        is LValueAst.IndexLValue -> lv.copy(
            target = substituteExpr(lv.target, sub),
            index = substituteExpr(lv.index, sub),
        )
        else -> sub.rewriteLValue(lv)
    }

    private fun substituteIfStmt(s: StmtAst.IfStmt, sub: Substitution) = s.copy(
        cond = substituteExpr(s.cond, sub),
        thenBody = substituteStmtList(s.thenBody, sub),
        elseBody = substituteStmtList(s.elseBody, sub),
    )

    private fun substituteTryStmt(s: StmtAst.TryStmt, sub: Substitution) = s.copy(
        tryBody = substituteStmtList(s.tryBody, sub),
        catchBody = substituteStmtList(s.catchBody, sub),
        finallyBody = substituteStmtList(s.finallyBody, sub),
    )
}
